/**
 * Floating ISI tray toggle.
 *
 * Control spec:
 *  - Single icon-only chevron button handles both directions; icon rotates.
 *  - aria-expanded + aria-controls on the button; aria-label swaps with state.
 *  - The whole collapsed bar is a secondary click affordance, but the button
 *    is the only element in the tab order and the only labelled control.
 *  - On expand: do NOT move focus or scroll the document — expand in place.
 *  - State does not persist across pages/reloads (no storage).
 *  - Reduced-motion is handled in CSS; JS only toggles state.
 */
export function initIsiTray() {
  const tray = document.querySelector('[data-emc-tray]');
  if (!tray) return;

  const button = tray.querySelector('[data-emc-tray-toggle]');
  const content = tray.querySelector('#emc-tray-content');
  const surface = tray.querySelector('[data-emc-tray-surface]');
  if (!button || !content) return;

  const setState = (expanded) => {
    button.setAttribute('aria-expanded', String(expanded));
    button.setAttribute(
      'aria-label',
      expanded
        ? button.dataset.labelCollapse || 'Collapse'
        : button.dataset.labelExpand || 'Expand'
    );
    tray.classList.toggle('is-expanded', expanded);
    // Keep the panel in the DOM so it can animate open/closed (CSS grid-rows);
    // use inert + aria-hidden (not [hidden]) so it stays out of the tab order
    // and a11y tree while collapsed without blocking the transition.
    content.toggleAttribute('inert', !expanded);
    content.setAttribute('aria-hidden', String(!expanded));
    // Notify analytics (Phase 6) without moving focus or scrolling.
    tray.dispatchEvent(
      new CustomEvent('emc:isi-tray', { detail: { expanded }, bubbles: true })
    );
  };

  const toggle = () => setState(button.getAttribute('aria-expanded') !== 'true');

  button.addEventListener('click', toggle);

  // Secondary affordance: clicking the bar (but not the button, and not a link
  // or other interactive child) toggles too.
  if (surface) {
    surface.addEventListener('click', (e) => {
      if (e.target.closest('[data-emc-tray-toggle], a, button')) return;
      toggle();
    });
  }

  // Always start collapsed on every load.
  setState(false);

  // Hide the floating tray while the full ISI block is in view — the safety
  // content is already on screen, so the persistent tray is redundant there.
  const fullIsi = document.getElementById('isi');
  if (fullIsi && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const hidden = entry.isIntersecting;
          tray.classList.toggle('is-hidden', hidden);
          if (hidden) {
            setState(false); // collapse before hiding
            tray.setAttribute('aria-hidden', 'true');
            button.setAttribute('tabindex', '-1');
          } else {
            tray.removeAttribute('aria-hidden');
            button.removeAttribute('tabindex');
          }
        });
      },
      // Trigger a little before the block fully enters, and keep hidden until
      // it has mostly left the viewport.
      { threshold: 0, rootMargin: '0px 0px -20% 0px' }
    );
    io.observe(fullIsi);
  }
}
